import { WSAlert } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { queriesInternalAccounts, queriesOnboarding } from "@query";
import { VerificationLevel } from "@wingspanhq/wingspan-api-sdk";
import { useAct } from "../../../shared/hooks/useAct";
import { verificationService } from "../../../shared/services/verificaiton";

export const BannerBankingVerification: React.FC = () => {
  const queryVerifications = queriesOnboarding.getOnboardingStatus.useQuery(
    undefined,
    { select: status => status.verifications }
  );
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();

  const hasPendingAccounts = useMemo(() => {
    const internalAccounts = queryInternalAccounts.data?.data ?? [];
    return internalAccounts.some(acc => acc.status === "Pending");
  }, [queryInternalAccounts.data]);

  if (queryVerifications.isLoading || queryInternalAccounts.isLoading) {
    return null;
  }

  return !queryVerifications.data ||
    queryVerifications.data?.banking === VerificationLevel.NONE ||
    queryVerifications.data?.banking === VerificationLevel.UPDATE_REQUIRED ? (
    <RequireAdditionalInfoAlert />
  ) : queryVerifications.data?.banking === VerificationLevel.PENDING ||
    queryVerifications.data?.banking === VerificationLevel.FAILED ||
    hasPendingAccounts ? (
    <WSAlert theme="info" icon="info-circle" title="Review in progress">
      Your information was received and our team is currently reviewing it. Once
      the information is approved, features such as Wingspan Wallet and Tax
      Withholdings can be activated. The review process typically takes less
      than one (1) business day. Thank you for your patience.
    </WSAlert>
  ) : null;
};

const RequireAdditionalInfoAlert: React.FC = () => {
  const history = useHistory();

  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();

  const pendingAccounts = useMemo(() => {
    const internalAccounts = queryInternalAccounts.data?.data ?? [];
    return internalAccounts.filter(acc => acc.status === "Pending");
  }, [queryInternalAccounts.data]);

  const hasRequestedWalletOnly = useMemo(() => {
    return (
      pendingAccounts.length === 1 && pendingAccounts[0].type === "Banking"
    );
  }, [pendingAccounts]);

  const hasRequestedTaxWithholdingOnly = useMemo(() => {
    return (
      pendingAccounts.length === 1 &&
      pendingAccounts[0].type === "TaxWithholding"
    );
  }, [pendingAccounts]);

  const { act, isLoading } = useAct(() =>
    verificationService.completeBankingVerification(history)
  );

  // If the user has requested both Wallet and TaxWithholding, we will show the Wallet alert
  let title = "Additional information needed to activate banking features";
  let description =
    "Additional information is required to use Wingspan Wallet or Tax Withholding, per banking regulations.";

  if (hasRequestedTaxWithholdingOnly) {
    title = "Additional information needed to activate Tax Withholding";
    description =
      "Additional information is required to use Tax Withholding, per banking regulations.";
  } else if (hasRequestedWalletOnly) {
    title = "Additional information needed to activate Wingspan Wallet";
    description =
      "Additional information is required to use Wingspan Wallet, per banking regulations.";
  }
  return (
    <WSAlert
      theme="warning"
      icon="alert"
      title={title}
      action={{
        loading: isLoading,
        text: "Complete additional information",
        onClick: act
      }}
    >
      {description}
    </WSAlert>
  );
};
